import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShoppingCart, Search, Plus, Minus, RotateCcw, Check, Package, TrendingUp, Clock, AlertCircle, Trash2, X, CalendarIcon, List, Filter, Download, Upload, FileSpreadsheet, Pencil, CalendarDays } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Branch codes
const BRANCHES = {
  "QC": "Quezon City",
  "PQ": "Parañaque",
  "CV": "Cavite",
  "PM": "Pampanga",
  "VZ": "Valenzuela",
  "OTG-A": "Orion On The Go - A",
  "OTG-B": "Orion On The Go - B",
  "OTG-C": "Orion On The Go - C"
};

export default function WalkInSales() {
  const { user, isAdmin } = useAuth();
  
  // Branch restriction logic - check both is_admin and can_access_all_branches
  const canAccessAllBranches = user?.can_access_all_branches || user?.is_admin || user?.role === "admin" || false;
  const userBranches = user?.assigned_branches?.length ? user.assigned_branches : (user?.assigned_branch ? [user.assigned_branch] : []);
  const userBranch = userBranches[0] || null;
  
  // Users without branch AND not admin = VIEW ONLY
  const canEdit = canAccessAllBranches || !!userBranch;
  
  const [selectedBranch, setSelectedBranch] = useState(() => {
    if (!canAccessAllBranches && userBranch) {
      return userBranch;
    }
    return "QC";
  });
  
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // CART STATE - multiple items per transaction
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [assistedBy, setAssistedBy] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [saleDate, setSaleDate] = useState(new Date()); // Custom date for sale
  const [saving, setSaving] = useState(false);
  
  // Recent sales and stats
  const [recentSales, setRecentSales] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Stock cache for items in cart
  const [stockCache, setStockCache] = useState({});
  
  // Delete modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // View All Records modal
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [recordsStartDate, setRecordsStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [recordsEndDate, setRecordsEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [allRecords, setAllRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [recordsStats, setRecordsStats] = useState({ total: 0, totalQty: 0 });
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState([]);
  
  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    quantity: 0,
    reference_no: "",
    sale_date: "",
    notes: ""
  });
  const [editSaving, setEditSaving] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importDate, setImportDate] = useState(new Date().toISOString().split('T')[0]); // Date for imported items
  const fileInputRef = useRef(null);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  const searchRef = useRef(null);

  // Bulk selection handlers
  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllForDate = (dateRecords) => {
    const ids = dateRecords.map(r => r.id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error("Wala kang napiling records");
      return;
    }
    
    if (!window.confirm(`Sigurado ka bang i-delete ang ${selectedIds.size} walk-in sales record(s)?\n\nLahat ng stocks ay BABALIK sa Display Area.`)) {
      return;
    }

    setBulkDeleting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(`${API}/walk-in-sales/bulk-delete`, {
        sale_ids: Array.from(selectedIds)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.errors > 0) {
        toast.warning(`${response.data.deleted} deleted, ${response.data.errors} failed. Stock restored: +${response.data.total_qty_restored}`);
      } else {
        toast.success(response.data.message);
      }
      
      clearSelection();
      await fetchAllRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Fetch item masterlist
  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/item-master/list`);
      setItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching items:", error);
      toast.error("Failed to load items");
    }
  }, []);

  // Fetch recent sales
  const fetchRecentSales = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/walk-in-sales`, {
        params: { branch: selectedBranch, limit: 10 }
      });
      setRecentSales(response.data.sales || []);
    } catch (error) {
      console.error("Error fetching sales:", error);
    }
  }, [selectedBranch]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/walk-in-sales/stats`, {
        params: { branch: selectedBranch, days: 30 }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [selectedBranch]);

  // Check stock for an item (Display Area ONLY for POS)
  const checkStock = async (itemName) => {
    if (!itemName || stockCache[itemName] !== undefined) return stockCache[itemName];
    
    try {
      const response = await axios.get(`${API}/inventory/stock-check/${selectedBranch}/${encodeURIComponent(itemName)}`);
      // POS uses ONLY Display Area stock - Stock Room is NOT sellable directly
      const stock = response.data.display_area_stock ?? 0;
      setStockCache(prev => ({ ...prev, [itemName]: stock }));
      return stock;
    } catch (error) {
      console.error("Error checking stock:", error);
      return 0;
    }
  };

  // Fetch all records with date range (for View All Records)
  const fetchAllRecords = async (startDate = null, endDate = null, loadAll = false) => {
    setLoadingRecords(true);
    try {
      const params = { 
        branch: selectedBranch, 
        limit: 1000
      };
      
      // If loading all, don't add date filters
      if (!loadAll) {
        const start = startDate || recordsStartDate;
        const end = endDate || recordsEndDate;
        if (start) params.start_date = start;
        if (end) params.end_date = end;
      }
      
      console.log("[Walk-In Records] Fetching with params:", params);
      
      const response = await axios.get(`${API}/walk-in-sales`, { params });
      const records = response.data.sales || [];
      
      console.log(`[Walk-In Records] Got ${records.length} records`);
      
      setAllRecords(records);
      
      // Calculate stats
      const totalQty = records.reduce((sum, r) => sum + (r.quantity || 0), 0);
      setRecordsStats({
        total: records.length,
        totalQty: totalQty
      });
    } catch (error) {
      console.error("Error fetching records:", error);
      setAllRecords([]);
      setRecordsStats({ total: 0, totalQty: 0 });
    } finally {
      setLoadingRecords(false);
    }
  };

  // Open records modal - load all records first
  const openRecordsModal = () => {
    setShowRecordsModal(true);
    fetchAllRecords(null, null, true); // Load ALL records initially
  };

  // Delete records for a specific date
  const [deletingDate, setDeletingDate] = useState(null);
  const handleDeleteByDate = async (dateToDelete) => {
    const recordsForDate = allRecords.filter(r => {
      const recDate = r.sale_date || (r.timestamp ? r.timestamp.split('T')[0] : 'No Date');
      return recDate === dateToDelete;
    });
    
    if (recordsForDate.length === 0) {
      toast.error("Walang records sa date na ito");
      return;
    }
    
    if (!window.confirm(`Delete ALL ${recordsForDate.length} records for ${dateToDelete}?\n\nIto ay mag-rereverse ng stock deductions para sa date na ito.`)) {
      return;
    }
    
    setDeletingDate(dateToDelete);
    const token = localStorage.getItem("auth_token");
    let successCount = 0;
    let failCount = 0;
    
    const progressToast = toast.loading(`Deleting ${dateToDelete}: 0/${recordsForDate.length}...`);
    
    try {
      for (let i = 0; i < recordsForDate.length; i++) {
        const sale = recordsForDate[i];
        
        if (i % 3 === 0 || i === recordsForDate.length - 1) {
          toast.loading(`Deleting ${dateToDelete}: ${i + 1}/${recordsForDate.length}...`, { id: progressToast });
        }
        
        try {
          await axios.delete(`${API}/walk-in-sales/${sale.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to delete ${sale.id}:`, err);
          failCount++;
        }
      }
      
      toast.dismiss(progressToast);
      
      if (failCount === 0) {
        toast.success(`✅ Deleted all ${successCount} records for ${dateToDelete}!`);
      } else {
        toast.warning(`Deleted ${successCount}, ${failCount} failed for ${dateToDelete}`);
      }
      
      // Refresh data - remove deleted records from state
      setAllRecords(prev => prev.filter(r => {
        const recDate = r.sale_date || (r.timestamp ? r.timestamp.split('T')[0] : 'No Date');
        return recDate !== dateToDelete;
      }));
      await fetchRecentSales();
      await fetchStats();
      
    } catch (error) {
      toast.dismiss(progressToast);
      toast.error("Delete failed: " + error.message);
    } finally {
      setDeletingDate(null);
    }
  };

  // Delete all records
  const handleDeleteAll = async () => {
    if (allRecords.length === 0) {
      toast.error("Walang records na ide-delete");
      return;
    }
    
    setDeletingAll(true);
    const token = localStorage.getItem("auth_token");
    let successCount = 0;
    let failCount = 0;
    
    const progressToast = toast.loading(`Deleting 0/${allRecords.length} sales...`);
    
    try {
      for (let i = 0; i < allRecords.length; i++) {
        const sale = allRecords[i];
        
        if (i % 5 === 0 || i === allRecords.length - 1) {
          toast.loading(`Deleting ${i + 1}/${allRecords.length} sales...`, { id: progressToast });
        }
        
        try {
          await axios.delete(`${API}/walk-in-sales/${sale.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to delete ${sale.id}:`, err);
          failCount++;
        }
        
        if (i > 0 && i % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      toast.dismiss(progressToast);
      
      if (failCount === 0) {
        toast.success(`✅ Deleted ${successCount} sales successfully!`);
      } else {
        toast.warning(`Deleted ${successCount} sales, ${failCount} failed`);
      }
      
      // Refresh data
      setShowDeleteAllConfirm(false);
      setAllRecords([]);
      await fetchRecentSales();
      await fetchStats();
      
    } catch (error) {
      toast.dismiss(progressToast);
      toast.error("Delete failed: " + error.message);
    } finally {
      setDeletingAll(false);
    }
  };

  // Export sales records to CSV - for easy encoding to spreadsheet
  const exportSalesRecords = () => {
    if (allRecords.length === 0) {
      toast.error("Walang records na i-export");
      return;
    }
    
    // CSV header matching user's spreadsheet format
    const headers = [
      "Encoder",
      "Date Received", 
      "Category",
      "Item Code",
      "Item",
      "Stocks Qty",
      "Location",
      "Date Pulled Out",
      "Notes",
      "Reference No",
      "Customer"
    ];
    
    // Convert records to CSV rows
    const rows = allRecords.map(record => [
      record.encoder || "",
      record.sale_date || "",
      "Walk-In Sale",
      record.item_code || "",
      record.item_name || "",
      record.quantity || 0,
      "Display Area",
      record.sale_date || "",
      record.notes || "",
      record.reference_no || "",
      record.customer_name || ""
    ]);
    
    // Build CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma
        const escaped = String(cell).replace(/"/g, '""');
        return escaped.includes(',') || escaped.includes('\n') ? `"${escaped}"` : escaped;
      }).join(","))
    ].join("\n");
    
    // Create download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Walk-In-Sales_${selectedBranch}_${recordsStartDate || 'all'}_to_${recordsEndDate || 'now'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`✅ Exported ${allRecords.length} records to CSV!`);
  };

  // Robust header detector — returns true ONLY if the first row is clearly a header
  // (no digits in any cell, AND 2+ exact matches against known header terms).
  // This prevents accidentally dropping the first data row when CSV has no header.
  const isHeaderRow = (cells) => {
    const exactHeaderTerms = new Set([
      'item', 'item name', 'item code', 'product', 'product name', 'description',
      'qty', 'quantity', 'stocks', 'stock', 'pcs',
      'customer', 'customer name', 'name',
      'date', 'sale date', 'amount', 'price', 'remarks', 'notes',
      '#', 'no', 'no.', 'row'
    ]);
    const nonEmpty = (cells || []).filter(c => c && String(c).trim());
    const hasDigits = nonEmpty.some(c => /\d/.test(String(c)));
    const exactMatches = nonEmpty.filter(c => exactHeaderTerms.has(String(c).toLowerCase().trim())).length;
    return exactMatches >= 2 && !hasDigits;
  };

  // Handle file upload for import (supports CSV and XLSX)
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let rows = [];
        let header = [];
        
        if (isExcel) {
          // Parse Excel file
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length === 0) {
            toast.error("File is empty");
            return;
          }
          
          // ROBUST header detection — only skip first row if it's clearly a header
          // (no digits in any cell, plus 2+ exact header keyword matches)
          const firstRow = (jsonData[0] || []).map(c => String(c || '').trim());
          if (isHeaderRow(firstRow)) {
            header = firstRow.map(h => h.toLowerCase());
            rows = jsonData.slice(1);
          } else {
            // No header — treat all rows as data, build a synthetic header from common positions
            console.log("[WalkIn Import] First row appears to be data, NOT a header. Including row 1.");
            header = ["item name", "qty"];  // assume 2-col format: item, qty
            rows = jsonData;
          }
        } else {
          // Parse CSV file
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length === 0) {
            toast.error("File is empty");
            return;
          }
          
          const firstRowCells = lines[0].split(',').map(c => c.trim().replace(/"/g, ''));
          if (isHeaderRow(firstRowCells)) {
            header = firstRowCells.map(h => h.toLowerCase());
            rows = lines.slice(1).map(line => line.split(',').map(c => c.trim().replace(/"/g, '')));
          } else {
            console.log("[WalkIn Import] First row appears to be data, NOT a header. Including row 1.");
            header = ["item name", "qty"];
            rows = lines.map(line => line.split(',').map(c => c.trim().replace(/"/g, '')));
          }
        }
        
        // Find column indices
        const itemNameIdx = header.findIndex(h => h.includes('item') || h.includes('name') || h.includes('product'));
        const qtyIdx = header.findIndex(h => h.includes('qty') || h.includes('quantity') || h.includes('stocks'));
        
        if (itemNameIdx === -1) {
          toast.error("Could not find 'Item Name' column in file");
          return;
        }
        
        // Parse data rows
        const parsed = [];
        const errors = [];
        
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const itemName = String(row[itemNameIdx] || '').trim();
          const qty = qtyIdx >= 0 ? parseFloat(row[qtyIdx]) || 1 : 1;
          
          if (!itemName) continue;
          
          // Try to find item in masterlist (case-insensitive + whitespace-normalized)
          const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
          const matchedItem = items.find(item => 
            norm(item.item_name) === norm(itemName) ||
            norm(item.item_code) === norm(itemName)
          );
          
          if (matchedItem) {
            parsed.push({
              item: matchedItem,
              quantity: qty,
              matched: true
            });
          } else {
            parsed.push({
              item: { item_name: itemName },
              quantity: qty,
              matched: false
            });
            errors.push(`Row ${i + 2}: "${itemName}" not found in item master`);
          }
        }
        
        setImportData(parsed);
        setImportErrors(errors);
        setShowImportModal(true);
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
      } catch (error) {
        console.error("Parse error:", error);
        toast.error("Failed to parse file. Make sure it's a valid CSV or Excel file.");
      }
    };
    
    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  // Add imported items to cart - WITH STOCK CHECK
  const importToCart = async () => {
    if (importing) return; // Prevent double-click
    
    const validItems = importData.filter(d => d.matched);
    
    if (validItems.length === 0) {
      toast.error("No valid items to import");
      return;
    }
    
    setImporting(true);
    
    try {
      // Check stock for all items first
      const itemsWithStock = [];
      for (const { item, quantity } of validItems) {
        const stock = await checkStock(item.item_name);
        itemsWithStock.push({
          ...item,
          quantity,
          available_stock: stock,
          importDate: importDate
        });
      }
      
      // Add each item to cart with stock info
      setCart(prev => {
        let newCart = [...prev];
        for (const itemData of itemsWithStock) {
          const existingIdx = newCart.findIndex(c => c.item_name === itemData.item_name);
          if (existingIdx >= 0) {
            newCart[existingIdx] = {
              ...newCart[existingIdx],
              quantity: newCart[existingIdx].quantity + itemData.quantity
            };
          } else {
            newCart.push(itemData);
          }
        }
        return newCart;
      });
      
      // Also set the sale date to the import date
      setSaleDate(new Date(importDate + 'T00:00:00'));
      
      toast.success(`✅ Added ${validItems.length} items to cart (Date: ${importDate})`);
      setShowImportModal(false);
      setImportData([]);
      setImportErrors([]);
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import items: " + error.message);
    } finally {
      setImporting(false);
    }
  };

  // Download template CSV
  const downloadTemplate = () => {
    const template = `Item Name,Quantity
Alpha White H4,2
Air All Weather HB4,1
Alpha All Weather H11,3`;
    
    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Walk-In-Sales-Import-Template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success("📥 Template downloaded!");
  };

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    fetchRecentSales();
    fetchStats();
    setStockCache({}); // Clear stock cache on branch change
  }, [selectedBranch, fetchRecentSales, fetchStats]);

  // Filter items based on search
  useEffect(() => {
    if (!searchQuery) {
      setFilteredItems([]);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = items.filter(item => 
        item.item_name?.toLowerCase().includes(query) ||
        item.item_code?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
      ).slice(0, 15);
      setFilteredItems(filtered);
    }
  }, [searchQuery, items]);

  // Add item to cart
  const addToCart = async (item) => {
    // Check if item already in cart
    const existingIdx = cart.findIndex(c => c.item_name === item.item_name);
    
    if (existingIdx >= 0) {
      // Increase quantity
      const newCart = [...cart];
      newCart[existingIdx].quantity += 1;
      setCart(newCart);
    } else {
      // Add new item with stock check
      const stock = await checkStock(item.item_name);
      setCart([...cart, {
        ...item,
        quantity: 1,
        available_stock: stock
      }]);
    }
    
    setSearchQuery("");
    setFilteredItems([]);
    searchRef.current?.focus();
  };

  // Update cart item quantity
  const updateCartQuantity = (index, newQty) => {
    if (newQty <= 0) {
      removeFromCart(index);
      return;
    }
    
    const newCart = [...cart];
    newCart[index].quantity = newQty;
    setCart(newCart);
  };

  // Remove item from cart
  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
    setCustomerName("");
    setAssistedBy("");
    setReferenceNo("");
    setNotes("");
  };

  // Calculate cart total
  const cartTotal = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Check if cart has stock issues
  const hasStockIssues = cart.some(item => item.quantity > (item.available_stock || 0));

  // Submit sale (all items in cart as one transaction)
  const handleSubmit = async () => {
    if (cart.length === 0) {
      toast.error("Walang item sa cart");
      return;
    }

    // Check stock for all items
    for (const item of cart) {
      if (item.quantity > (item.available_stock || 0)) {
        toast.error(`Hindi sapat ang stock para sa ${item.item_name}! Available: ${item.available_stock || 0}`);
        return;
      }
    }

    setSaving(true);
    try {
      // Generate a transaction reference for this cart
      const txnRef = referenceNo || `TXN-${Date.now()}`;
      
      // Save all items in cart as a batch
      const response = await axios.post(`${API}/walk-in-sales/batch`, {
        branch: selectedBranch,
        items: cart.map(item => ({
          item_id: item.id || "",
          item_name: item.item_name,
          item_code: item.item_code || "",
          category: item.category || "",
          quantity: item.quantity
        })),
        encoder: user?.name || user?.email?.split('@')[0] || "System",
        customer_name: customerName,
        assisted_by: assistedBy || user?.name || user?.email?.split('@')[0] || "",
        notes: notes,
        reference_no: txnRef,
        sale_date: format(saleDate, "yyyy-MM-dd") // Custom date
      });

      // Check if there are any errors in the response (partial save)
      const errors = response.data.errors;
      const savedCount = response.data.saved_count || 0;
      const totalItems = cart.length;
      
      if (errors && errors.length > 0) {
        // Some items FAILED to save - SHOW BIG WARNING
        toast.error(
          <div className="text-left">
            <div className="font-bold text-red-700">⚠️ MAY {errors.length} ITEM(S) NA HINDI NA-SAVE!</div>
            <div className="text-sm mt-1">Na-save: {savedCount} out of {totalItems} items</div>
            <div className="text-sm mt-2 font-semibold text-red-600">Mga item na HINDI na-save:</div>
            <ul className="text-xs mt-1 space-y-1">
              {errors.slice(0, 5).map((err, i) => (
                <li key={i} className="bg-red-100 p-1 rounded">❌ {err}</li>
              ))}
              {errors.length > 5 && <li className="text-gray-500">...at {errors.length - 5} pa</li>}
            </ul>
            <div className="text-xs mt-2 text-red-700">I-check kung may stock sa Display Area!</div>
          </div>,
          { duration: 20000 }
        );
      } else {
        toast.success(`✅ ${savedCount} item(s) saved! Total: ${response.data.total_quantity} units`);
      }
      
      // Clear cart and refresh data
      clearCart();
      await fetchRecentSales();
      await fetchStats();
      setStockCache({});
      
    } catch (error) {
      console.error("Error saving sale:", error);
      // Show detailed error message
      const errorDetail = error.response?.data?.detail || error.response?.data?.message || error.message || "Failed to save sale";
      const errors = error.response?.data?.errors;
      
      if (errors && errors.length > 0) {
        // Show specific item errors
        toast.error(`${errorDetail}\n\nItems na may problema:\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? `\n...at ${errors.length - 3} pa` : ''}`, {
          duration: 10000
        });
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setSaving(false);
    }
  };

  // Delete/Void sale handlers
  const openDeleteConfirm = (sale) => {
    setSaleToDelete(sale);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!saleToDelete) return;
    
    const saleId = saleToDelete.id || saleToDelete._id;
    if (!saleId) {
      toast.error("Cannot delete: sale ID missing. Refresh and try again.");
      setShowDeleteConfirm(false);
      setSaleToDelete(null);
      return;
    }
    
    setDeleting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.delete(`${API}/walk-in-sales/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message || "Sale deleted successfully");
      setShowDeleteConfirm(false);
      setSaleToDelete(null);
      
      // Wrap refreshes so refresh failure doesn't whitescreen
      try { await fetchRecentSales(); } catch (e) { console.error("Refresh sales failed:", e); }
      try { await fetchStats(); } catch (e) { console.error("Refresh stats failed:", e); }
    } catch (error) {
      // Better error visibility — show HTTP status + detail + fallback
      console.error("Walk-in delete error:", error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.response?.data?.message;
      const msg = detail
        ? `Delete failed (${status}): ${detail}`
        : status
          ? `Delete failed: HTTP ${status} — ${error.message}`
          : `Delete failed: ${error.message || 'Network error — check internet connection'}`;
      toast.error(msg, { duration: 7000 });
    } finally {
      setDeleting(false);
    }
  };

  // Open edit modal with record data
  const openEditModal = (record) => {
    setEditingRecord(record);
    setEditForm({
      quantity: record.quantity || 0,
      reference_no: record.reference_no || "",
      sale_date: record.sale_date || new Date().toISOString().split('T')[0],
      notes: record.notes || ""
    });
    setShowEditModal(true);
  };

  // Save edited record
  const handleEditSave = async () => {
    if (!editingRecord) return;
    
    setEditSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.put(`${API}/walk-in-sales/${editingRecord.id}`, {
        quantity: editForm.quantity,
        reference_no: editForm.reference_no,
        sale_date: editForm.sale_date,
        notes: editForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Record updated successfully!");
      setShowEditModal(false);
      setEditingRecord(null);
      
      // Refresh data
      await fetchRecentSales();
      await fetchStats();
      if (showRecordsModal) {
        await fetchAllRecords();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" data-testid="walk-in-sales-page">
      {/* VIEW ONLY Banner for users without branch */}
      {!canEdit && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">VIEW ONLY MODE - Your email does not have a branch assigned. Contact admin to enable sales recording.</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-green-600 flex items-center justify-center">
              <ShoppingCart className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Walk-In Sales</h1>
              <p className="text-sm text-gray-500">Cart-style POS para sa walk-in customers</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={selectedBranch} onValueChange={setSelectedBranch} disabled={!canAccessAllBranches && userBranches.length <= 1}>
              <SelectTrigger className="w-[180px]" data-testid="branch-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BRANCHES).map(([code, name]) => (
                  <SelectItem 
                    key={code} 
                    value={code}
                    disabled={!canAccessAllBranches && !userBranches.includes(code)}
                  >
                    {code} - {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!canAccessAllBranches && userBranches.length > 0 && (
              <span className="text-xs text-orange-600">🔒 {userBranches.join(", ")}</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Item Search & Cart */}
        <div className="lg:col-span-2 space-y-4">
          {/* Item Search */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search & Add Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  ref={searchRef}
                  placeholder="Type item name to search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 text-lg"
                  data-testid="item-search-input"
                />
                
                {/* Autocomplete Dropdown */}
                {filteredItems.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {filteredItems.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => canEdit && addToCart(item)}
                        disabled={!canEdit}
                        className={`w-full px-4 py-3 text-left flex items-center justify-between border-b last:border-0 ${canEdit ? 'hover:bg-green-50' : 'opacity-50 cursor-not-allowed'}`}
                        data-testid={`item-option-${idx}`}
                      >
                        <div>
                          <div className="font-medium text-gray-900">{item.item_name}</div>
                          <div className="text-sm text-gray-500">{item.category}</div>
                        </div>
                        <Plus className={`h-5 w-5 ${canEdit ? 'text-green-600' : 'text-gray-400'}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Shopping Cart */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-green-600" />
                  Cart ({cart.length} items)
                </CardTitle>
                <div className="flex items-center gap-2">
                  {/* Import Button */}
                  <div className="relative">
                    <input
                      type="file"
                      accept=".csv,.txt,.xlsx,.xls"
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                      className="hidden"
                      id="import-file"
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => fileInputRef.current?.click()}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      title="Import items from CSV or Excel"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Import
                    </Button>
                  </div>
                  {cart.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {cart.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Walang item sa cart</p>
                  <p className="text-sm">Search and add items above</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        item.quantity > (item.available_stock || 0) 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-200 bg-white'
                      }`}
                      data-testid={`cart-item-${idx}`}
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{item.item_name}</div>
                        <div className="text-sm text-gray-500">
                          Stock: <span className={item.available_stock > 0 ? 'text-green-600' : 'text-red-600'}>
                            {item.available_stock || 0}
                          </span>
                          {item.quantity > (item.available_stock || 0) && (
                            <span className="ml-2 text-red-600 font-medium">
                              ⚠️ Kulang ang stock!
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Quantity Controls - Support 0.5 for single bulb */}
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateCartQuantity(idx, item.quantity - 0.5)}
                          data-testid={`decrease-qty-${idx}`}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={item.quantity}
                          onChange={(e) => updateCartQuantity(idx, parseFloat(e.target.value) || 0)}
                          className="w-20 text-center font-bold"
                          data-testid={`qty-input-${idx}`}
                        />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateCartQuantity(idx, item.quantity + 0.5)}
                          data-testid={`increase-qty-${idx}`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => removeFromCart(idx)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`remove-item-${idx}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Cart Summary */}
                  <div className="pt-3 border-t">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>Total Items:</span>
                      <span className="text-green-600">{cartTotal}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Info & Submit */}
          {cart.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Transaction Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Customer Name</label>
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Optional"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Assisted By</label>
                    <Input
                      value={assistedBy}
                      onChange={(e) => setAssistedBy(e.target.value)}
                      placeholder={user?.name || user?.email?.split('@')[0] || "Staff"}
                      data-testid="assisted-by-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Reference No.</label>
                    <Input
                      value={referenceNo}
                      onChange={(e) => setReferenceNo(e.target.value)}
                      placeholder="Auto-generated if blank"
                      data-testid="reference-no-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Sale Date</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="sale-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(saleDate, "MMM dd, yyyy")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={saleDate}
                          onSelect={(date) => date && setSaleDate(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes"
                    data-testid="notes-input"
                  />
                </div>
                
                <Button
                  onClick={handleSubmit}
                  disabled={saving || cart.length === 0 || hasStockIssues}
                  className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                  data-testid="save-sale-btn"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <RotateCcw className="h-5 w-5 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Check className="h-5 w-5" />
                      Save Sale ({cart.length} items, {cartTotal} units)
                    </span>
                  )}
                </Button>
                
                {hasStockIssues && (
                  <div className="flex items-center gap-2 text-red-600 text-sm justify-center">
                    <AlertCircle className="h-4 w-4" />
                    May item na kulang ang stock. Please adjust quantity.
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Stats & Recent Sales */}
        <div className="space-y-4">
          {/* Today's Stats */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Today&apos;s Sales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {stats?.today_count || 0}
                  </div>
                  <div className="text-sm text-gray-500">Transactions</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats?.today_units || 0}
                  </div>
                  <div className="text-sm text-gray-500">Units Sold</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* View All Records Button */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="py-4">
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={openRecordsModal}
              >
                <List className="h-4 w-4 mr-2" />
                View All Records by Date
              </Button>
            </CardContent>
          </Card>

          {/* Recent Sales */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Recent Sales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {recentSales.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No recent sales
                  </div>
                ) : (
                  recentSales.map((sale, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 group"
                      data-testid={`recent-sale-${idx}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 text-sm">{sale.item_name}</div>
                          <div className="text-xs text-gray-500">
                            Qty: {sale.quantity} • {sale.encoder} • {sale.sale_date || 'No date'}
                          </div>
                          {sale.reference_no && (
                            <div className="text-xs text-blue-600">Ref: {sale.reference_no}</div>
                          )}
                        </div>
                        {isAdmin() && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDeleteConfirm(sale)}
                            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 h-7"
                            data-testid={`delete-sale-${idx}`}
                            disabled={!canEdit}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Sale
            </DialogTitle>
          </DialogHeader>
          {saleToDelete && (
            <div className="py-4">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete this sale? This will reverse the stock deduction.
              </p>
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="font-medium">{saleToDelete.item_name}</div>
                <div className="text-sm text-gray-500">
                  Qty: {saleToDelete.quantity} • {saleToDelete.timestamp?.split('T')[0]}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? "Deleting..." : "Delete Sale"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View All Records Modal */}
      <Dialog open={showRecordsModal} onOpenChange={setShowRecordsModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5 text-green-600" />
              Walk-In Sales Records - {selectedBranch}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Date Range Filter */}
            <div className="flex flex-wrap items-center gap-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 mb-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">From:</span>
              </div>
              <Input
                type="date"
                value={recordsStartDate}
                onChange={(e) => setRecordsStartDate(e.target.value)}
                className="w-36"
              />
              <span className="text-sm font-medium text-green-700">To:</span>
              <Input
                type="date"
                value={recordsEndDate}
                onChange={(e) => setRecordsEndDate(e.target.value)}
                className="w-36"
              />
              <Button
                onClick={() => fetchAllRecords(recordsStartDate, recordsEndDate, false)}
                disabled={loadingRecords}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                {loadingRecords ? <RotateCcw className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
                <span className="ml-1">Filter</span>
              </Button>
              <Button
                onClick={() => fetchAllRecords(null, null, true)}
                disabled={loadingRecords}
                size="sm"
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-100"
              >
                <List className="h-4 w-4 mr-1" />
                All
              </Button>
              
              {/* Stats */}
              <div className="ml-auto flex items-center gap-2">
                <div className="text-center px-3 py-1 bg-white rounded-lg border shadow-sm">
                  <div className="text-lg font-bold text-green-600">{recordsStats.total}</div>
                  <div className="text-xs text-gray-500">Records</div>
                </div>
                <div className="text-center px-3 py-1 bg-white rounded-lg border shadow-sm">
                  <div className="text-lg font-bold text-emerald-600">{recordsStats.totalQty}</div>
                  <div className="text-xs text-gray-500">Total Qty</div>
                </div>
                {/* Export Button */}
                <Button
                  onClick={() => exportSalesRecords()}
                  disabled={allRecords.length === 0}
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="export-sales-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export CSV
                </Button>
              </div>
            </div>
            
            {/* Bulk Selection Toolbar — appears only when rows are selected */}
            {selectedIds.size > 0 && canEdit && (
              <div className="mb-3 p-3 bg-red-50 border-2 border-red-300 rounded-lg flex items-center justify-between" data-testid="walkin-bulk-toolbar">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-red-800">
                    {selectedIds.size} record{selectedIds.size > 1 ? 's' : ''} selected
                  </span>
                  <span className="text-xs text-red-600">
                    (Lahat ng stock ay BABALIK sa Display Area kapag dinelete)
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearSelection}
                    className="border-gray-400"
                    data-testid="walkin-bulk-clear-btn"
                  >
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="walkin-bulk-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {bulkDeleting ? "Deleting..." : `Delete ${selectedIds.size} Selected`}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Records Table - Grouped by Date */}
            <div className="flex-1 overflow-auto border rounded-lg">
              {loadingRecords ? (
                <div className="flex items-center justify-center h-32">
                  <RotateCcw className="h-8 w-8 animate-spin text-green-600" />
                </div>
              ) : allRecords.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                  <Package className="h-12 w-12 mb-2 opacity-50" />
                  <p>No records found</p>
                </div>
              ) : (() => {
                // Group records by date
                const groupedByDate = allRecords.reduce((groups, record) => {
                  const date = record.sale_date || 'No Date';
                  if (!groups[date]) {
                    groups[date] = [];
                  }
                  groups[date].push(record);
                  return groups;
                }, {});
                
                // Sort dates in descending order (newest first)
                const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
                  if (a === 'No Date') return 1;
                  if (b === 'No Date') return -1;
                  return b.localeCompare(a);
                });
                
                return (
                  <div className="divide-y">
                    {sortedDates.map(date => (
                      <div key={date} className="bg-white">
                        {/* Date Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-green-50 to-green-100 px-4 py-2 border-b-2 border-green-200 z-10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={groupedByDate[date].length > 0 && groupedByDate[date].every(r => selectedIds.has(r.id))}
                                onChange={() => toggleSelectAllForDate(groupedByDate[date])}
                                className="h-4 w-4 cursor-pointer accent-green-600"
                                title="Select all for this date"
                                data-testid={`select-all-date-${date}`}
                              />
                              <CalendarDays className="h-4 w-4 text-green-600" />
                              <span className="font-bold text-green-800">{date}</span>
                              <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                                {groupedByDate[date].length} records
                              </span>
                            </div>
                            {/* Delete Date Button */}
                            {canEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteByDate(date)}
                                disabled={deletingDate === date}
                                className="h-7 px-2 text-red-600 border-red-300 hover:bg-red-50"
                              >
                                {deletingDate === date ? (
                                  <div className="animate-spin h-3 w-3 border-2 border-red-600 border-t-transparent rounded-full" />
                                ) : (
                                  <>
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Delete Date
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Records for this date */}
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr className="border-b">
                              <th className="px-3 py-2 text-center font-medium text-gray-700 w-10">
                                <span className="sr-only">Select</span>
                              </th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Item Name</th>
                              <th className="px-3 py-2 text-center font-medium text-gray-700">Qty</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Reference</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Encoder</th>
                              <th className="px-3 py-2 text-center font-medium text-gray-700">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {groupedByDate[date].map((record, idx) => (
                              <tr key={idx} className={`hover:bg-gray-50 ${selectedIds.has(record.id) ? 'bg-red-50' : ''}`}>
                                <td className="px-3 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.has(record.id)}
                                    onChange={() => toggleSelectId(record.id)}
                                    disabled={!canEdit}
                                    className="h-4 w-4 cursor-pointer accent-red-600"
                                    data-testid={`select-row-${record.id}`}
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-medium text-gray-900">{record.item_name}</div>
                                  <div className="text-xs text-gray-500">{record.item_code}</div>
                                </td>
                                <td className="px-3 py-2 text-center font-bold text-green-600">{record.quantity}</td>
                                <td className="px-3 py-2 text-xs text-gray-600">{record.reference_no || '-'}</td>
                                <td className="px-3 py-2 text-xs text-gray-600">{record.encoder}</td>
                                <td className="px-3 py-2 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEditModal(record)}
                                      className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-7"
                                      disabled={!canEdit}
                                      title="Edit"
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setSaleToDelete(record);
                                        setShowDeleteConfirm(true);
                                      }}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7"
                                      disabled={!canEdit}
                                      title="Delete"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            
            {/* Delete All Button */}
            {allRecords.length > 0 && canEdit && (
              <div className="pt-4 border-t mt-4">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setShowDeleteAllConfirm(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All {allRecords.length} Records
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={showDeleteAllConfirm} onOpenChange={setShowDeleteAllConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete All Walk-In Sales
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{allRecords.length}</div>
                <div className="text-red-700">sales will be deleted</div>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              ⚠️ This will delete ALL {allRecords.length} walk-in sales and return the stock to Display Area.
            </p>
            <p className="text-red-600 text-sm mt-2 font-medium">
              This action cannot be undone!
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteAllConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAll}
              disabled={deletingAll}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingAll ? (
                <span className="flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                `Delete All ${allRecords.length} Sales`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-blue-600" />
              Import Items to Cart
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Download Template */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-blue-700">Need a template?</div>
                  <div className="text-xs text-blue-600">Download sample CSV format</div>
                </div>
                <Button size="sm" variant="outline" onClick={downloadTemplate} className="text-blue-600 border-blue-300">
                  <Download className="h-4 w-4 mr-1" />
                  Template
                </Button>
              </div>
            </div>
            
            {/* Date Picker for Import */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-amber-700">📅 Sale/Pull Out Date</div>
                  <div className="text-xs text-amber-600">Anong date yung order na ito?</div>
                </div>
                <Input
                  type="date"
                  value={importDate}
                  onChange={(e) => setImportDate(e.target.value)}
                  className="w-40 border-amber-300"
                />
              </div>
            </div>
            
            {/* Import Preview */}
            {importData.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">Preview ({importData.length} items)</span>
                  <span className="text-sm">
                    <span className="text-green-600">{importData.filter(d => d.matched).length} matched</span>
                    {importData.filter(d => !d.matched).length > 0 && (
                      <span className="text-red-500 ml-2">{importData.filter(d => !d.matched).length} not found</span>
                    )}
                  </span>
                </div>
                
                <div className="max-h-60 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Item Name</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {importData.map((row, idx) => (
                        <tr key={idx} className={row.matched ? 'bg-green-50' : 'bg-red-50'}>
                          <td className="px-3 py-2">
                            {row.matched ? (
                              <span className="text-green-600">✓</span>
                            ) : (
                              <span className="text-red-500">✗</span>
                            )}
                          </td>
                          <td className="px-3 py-2">{row.item.item_name}</td>
                          <td className="px-3 py-2 text-center">{row.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {importErrors.length > 0 && (
                  <div className="p-2 bg-amber-50 rounded border border-amber-200 text-xs text-amber-700">
                    <div className="font-medium mb-1">⚠️ Some items not found:</div>
                    <ul className="list-disc list-inside">
                      {importErrors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {importErrors.length > 5 && (
                        <li>...and {importErrors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {importData.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Upload className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Upload a CSV file to import items</p>
                <p className="text-sm mt-1">Required column: "Item Name" or "Item"</p>
                <p className="text-sm">Optional column: "Quantity" or "Qty"</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportModal(false); setImportData([]); setImportErrors([]); }} disabled={importing}>
              Cancel
            </Button>
            <Button 
              onClick={importToCart}
              disabled={importData.filter(d => d.matched).length === 0 || importing}
              className="bg-green-600 hover:bg-green-700"
            >
              {importing ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 mr-1" />
                  Add {importData.filter(d => d.matched).length} to Cart
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Record Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Edit Record
            </DialogTitle>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-4">
              {/* Item Info (Read-only) */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-semibold text-gray-900">{editingRecord.item_name}</div>
                <div className="text-sm text-gray-500">{editingRecord.item_code}</div>
              </div>

              {/* Editable Fields */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Quantity</label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={editForm.quantity}
                  onChange={(e) => setEditForm(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                  className="text-lg font-bold"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Reference No.</label>
                <Input
                  value={editForm.reference_no}
                  onChange={(e) => setEditForm(prev => ({ ...prev, reference_no: e.target.value }))}
                  placeholder="OR #, Receipt #, etc."
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Sale Date</label>
                <Input
                  type="date"
                  value={editForm.sale_date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, sale_date: e.target.value }))}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Notes</label>
                <Input
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button 
              onClick={handleEditSave}
              disabled={editSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editSaving ? <RotateCcw className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
